import React, { useMemo } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import UnderProgress from 'icons/under-progress.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useTrackedPersonnel from 'components/hooks/useTrackedPersonnel';

import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';
import DotSkeleton from 'components/application/tableSkeletons/DotSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import PersonnelTrainingStatusIndicator from 'components/personnel/PersonnelTrainingStatusIndicator';

import { sortRolesByPrimaryAndPosition } from 'components/helpers/personnel';
import { personDisplayName } from 'components/helpers/users';

export default function PersonnelTable({
  domainAssignableCompanies,
  domainPersonnel,
  isActionsColumnVisible,
  isArchived,
  isDataDisplayable,
  onRowClick,
  onUnarchiveClick,
  personnelIdsOfProfilePhotoUnderProgress,
}) {
  const currentActor = useCurrentActor();

  const { hasReachedTrackedPersonnelLimit } = useTrackedPersonnel();
  const { hasTeamViewableAccess } = useTrainingRegisterResources();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const buildColumnsArgs = {
    hasTeamViewableAccess,
    isActionsColumnVisible,
    isArchived,
    isTrainingRegisterEnabled:
      currentActor.isAllowedFeature('training_register'),
    translations: {
      cells: {
        unarchive: i18nTShared('unarchive'),
        updateInProgress: i18nTShared('updateInProgress'),
      },
      headers: {
        actions: i18nTShared('actions'),
        company: i18nTShared('company'),
        name: i18nTShared('name'),
        role: i18nTShared('role'),
        status: i18nTShared('status'),
        team: i18nTShared('team'),
      },
    },
  };

  const columns = useMemo(
    () => buildColumns(buildColumnsArgs),
    [JSON.stringify(buildColumnsArgs)],
  );

  const data = useMemo(() => {
    const personnel = domainPersonnel?.data || [];

    return isDataDisplayable ?
        Array(personnel.length || 25).fill({})
      : personnel;
  }, [domainPersonnel?.data, isDataDisplayable]);

  const included = useMemo(() => {
    const includedResources = domainPersonnel?.included || {};

    // Include data from assignable companies response (presumably made separately so it can be reused
    // for the sidepanel dropdown) as companies and subcontractors in the included resources
    includedResources.company = {};
    includedResources.subcontractor = {};

    domainAssignableCompanies?.data.forEach((assignableCompany) => {
      includedResources[
        assignableCompany.attributes.resourceType.toLowerCase()
      ][assignableCompany.id] = assignableCompany;
    });

    return includedResources;
  }, [domainAssignableCompanies?.data, domainPersonnel?.included]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    meta: {
      currentUserId: currentActor.user.id,
      hasReachedTrackedPersonnelLimit,
      included,
      isDataDisplayable,
      isELearningEnabled: currentActor.isAllowedFeature('e_learning'),
      onRowClick,
      onUnarchiveClick,
      personnelIdsOfProfilePhotoUnderProgress,
    },
  });

  return (
    <table className='tw-w-full tw-table-fixed tw-border-separate tw-select-none tw-rounded-b-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
      <thead className='tw-bg-grey-050'>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                className={header.column.columnDef.meta?.classNames}
                key={header.id}
              >
                <div
                  className={classNames(
                    'tw-flex tw-h-10 tw-items-center tw-justify-start tw-truncate tw-p-2 tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900',
                    header.column.getIsFirstColumn() &&
                      header.column.columnDef.id !== 'name' &&
                      'tw-pl-5',
                    header.column.columnDef.id === 'name' &&
                      (header.column.getIsFirstColumn() ?
                        'tw-pl-15'
                      : 'tw-pl-10'),
                  )}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr
            className='tw-group/row tw-bg-white hover:tw-bg-grey-025 has-[button:hover]:tw-bg-blue-025'
            key={row.id}
          >
            {row.getVisibleCells().map((cell) => (
              <td
                className={classNames(
                  'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
                  cell.column.columnDef.meta?.classNames,
                  cell.column.getIsFirstColumn() &&
                    cell.column.columnDef.id === 'name' &&
                    'tw-pl-5',
                )}
                key={cell.id}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function buildColumns({
  hasTeamViewableAccess,
  isActionsColumnVisible,
  isArchived,
  isTrainingRegisterEnabled,
  translations,
}) {
  const columns = [
    {
      id: 'status',
      header: translations.headers.status,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataDisplayable)
          return (
            <div className='tw-pl-4 tw-pr-2'>
              <DotSkeleton />
            </div>
          );

        return (
          <div
            className='tw-flex tw-h-full tw-items-center tw-justify-center tw-pl-4 tw-pr-2'
            onClick={() => table.options.meta.onRowClick(row.original.id)}
          >
            <PersonnelTrainingStatusIndicator
              status={
                isArchived ? 'archived' : row.original.attributes.trainingStatus
              }
            />
          </div>
        );
      },
      meta: { classNames: 'tw-w-16.5' },
    },
    {
      id: 'name',
      header: translations.headers.name,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataDisplayable) return <TextSkeleton />;

        const profilePhoto =
          table.options.meta.included.profilePhoto[
            row.original.relationships.profilePhoto.data?.id
          ];

        return (
          <div
            className='tw-flex tw-items-center tw-justify-start tw-gap-2'
            onClick={() => table.options.meta.onRowClick(row.original.id)}
          >
            {(
              table.options.meta.personnelIdsOfProfilePhotoUnderProgress.has(
                row.original.id,
              )
            ) ?
              <Tooltip
                placement='bottom'
                tooltip={translations.cells.updateInProgress}
                trigger='hover'
              >
                <div className='tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-grey-200'>
                  <div
                    className='tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center'
                    data-element-name='under-progress'
                  >
                    <UnderProgress />
                  </div>
                </div>
              </Tooltip>
            : <ProfilePhotoDisplay
                componentSize='sm'
                photoUrl={profilePhoto?.links?.croppedUrl}
                profileInitials={`${row.original.attributes.firstName[0]}${row.original.attributes.lastName[0]}`}
              />
            }
            <span className='tw-truncate tw-text-m tw-font-medium tw-text-grey-900'>
              {personDisplayName({ attributes: row.original.attributes })}
            </span>
          </div>
        );
      },
    },
    {
      id: 'role',
      header: translations.headers.role,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataDisplayable) return <TextSkeleton />;

        const userCompanyRoles =
          row.original.relationships.userCompanyRoles.data.map(
            (userCompanyRole) =>
              table.options.meta.included.userCompanyRole[userCompanyRole.id],
          );

        const companyRoles = row.original.relationships.companyRoles.data.map(
          (companyRole) =>
            table.options.meta.included.companyRole[companyRole.id],
        );

        const roles = sortRolesByPrimaryAndPosition(
          userCompanyRoles,
          companyRoles,
        );

        return (
          <div
            className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'
            onClick={() => table.options.meta.onRowClick(row.original.id)}
          >
            {roles.map((role) => role.attributes.position).join(' + ')}
          </div>
        );
      },
    },
    {
      id: 'team',
      header: translations.headers.team,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataDisplayable) return <TextSkeleton />;

        const teams = row.original.relationships.teams.data.map(
          (team) => table.options.meta.included.team[team.id],
        );

        return (
          <div
            className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'
            onClick={() => table.options.meta.onRowClick(row.original.id)}
          >
            {[...teams]
              .map((team) => team.attributes.name)
              .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
              .join(', ')}
          </div>
        );
      },
    },
    {
      id: 'company',
      header: translations.headers.company,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataDisplayable) return <TextSkeleton />;

        const company =
          (row.original.relationships.subcontractor.data?.id &&
            table.options.meta.included.subcontractor[
              row.original.relationships.subcontractor.data.id
            ]) ||
          (row.original.relationships.company.data?.id &&
            table.options.meta.included.company[
              row.original.relationships.company.data.id
            ]);

        return (
          <div
            className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'
            onClick={() => table.options.meta.onRowClick(row.original.id)}
          >
            {company?.attributes.name}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: translations.headers.actions,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataDisplayable) return null;

        if (isArchived) {
          return (
            <a
              className='app-link tw-inline-block tw-align-middle tw-text-m tw-font-medium tw-tracking-auto tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
              onClick={() => {
                table.options.meta.onUnarchiveClick({
                  personnelId: row.original.id,
                });
              }}
            >
              {translations.cells.unarchive}
            </a>
          );
        }

        const isActionsCountVisible =
          table.options.meta.hasReachedTrackedPersonnelLimit ?
            row.original.meta.isTracked
          : row.original.meta.isTrainingWritable;

        const isSelf =
          table.options.meta.currentUserId ===
          row.original.relationships.user.data?.id;

        const actionsCount =
          isActionsCountVisible ?
            row.original.meta.trainingRequiringEvidenceCount +
            row.original.meta.coursesRequiringBookingCount +
            (isSelf &&
              table.options.meta.isELearningEnabled &&
              row.original.meta.eLearningBookingsCount)
          : 0;

        return isActionsCountVisible && actionsCount > 0 ?
            <div className='tw-flex tw-h-full tw-items-center tw-justify-center'>
              <div className='tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-sm tw-bg-cyan-025 tw-text-cyan-800'>
                {actionsCount}
              </div>
            </div>
          : null;
      },
      meta: { classNames: isArchived ? 'tw-w-24' : 'tw-w-18' },
    },
  ];

  const visibleColumns = ['name', 'role', 'company'];
  if (isTrainingRegisterEnabled) visibleColumns.push('status');
  if (hasTeamViewableAccess) visibleColumns.push('team');
  if (isActionsColumnVisible) visibleColumns.push('actions');

  return columns.filter((column) => visibleColumns.includes(column.id));
}

PersonnelTable.propTypes = {
  domainAssignableCompanies: PropTypes.object,
  domainPersonnel: PropTypes.object,
  isActionsColumnVisible: PropTypes.bool,
  isArchived: PropTypes.bool,
  isDataDisplayable: PropTypes.bool,
  onRowClick: PropTypes.func.isRequired,
  onUnarchiveClick: PropTypes.func.isRequired,
  personnelIdsOfProfilePhotoUnderProgress: PropTypes.object,
};
